import format from 'date-fns/format'

import { isHatchDisabledForServiceType } from '@providers/booking/helper'
import { TBookingContextDef } from '@providers/booking/types'
import { buildPetGroups, buildPetGroupsTempered, sortOrders } from '@shared/booking_groups'
import type { TRouteNames } from 'app/types'
import { hasRequestDates } from 'components-ui/src/shared/location'
import { ClientBookingSearchService } from 'service-api/src/services/client_booking_search.service'
import { captureException } from 'service-api/src/shared/monitoring'
import { BookingSearchByDaysAdminDto, BookingSearchClientDto } from 'service-api/src/types/api'
import { TBookingSearchOptions, TBookingSearchPetGroup } from 'service-api/src/types/general'
import { shouldShowArrivalDeparturesBookingStep } from './booking_arrival_departures_flow'
import { hasGroomingServiceType, hasGroomsSelected } from './offers'
import { daycareSelectionPeriod } from './pet_addons'

const routeAfterBookingConfiguration = (bookingCtx: TBookingContextDef): TRouteNames => {
  if (bookingCtx.state.cart.bookWaitlist) {
    return 'waitlistConfirm'
  }

  return shouldShowArrivalDeparturesBookingStep(bookingCtx.state) ? 'bookingArrivalDepartures' : 'detailsPolicies'
}

class BookingSearchError extends Error {
  constructor(message: string, originalError: Error) {
    super(message)
    this.name = 'BookingSearchError'
    this.stack = originalError.stack
  }
}

export const backNavigation = (bookingCtx: TBookingContextDef): Promise<TRouteNames | undefined> => {
  const { state } = bookingCtx

  switch (bookingCtx.state.currentState) {
    case 'detailsPolicies': {
      if (state.serviceTypeName === 'temper-test') {
        return Promise.resolve('temperTest')
      }
      if (!(state.data.serviceType?.serviceType?.isVisit ?? false)) {
        return Promise.resolve('searchOffersResults')
      }
      if (shouldShowArrivalDeparturesBookingStep(bookingCtx.state)) {
        return Promise.resolve('bookingArrivalDepartures')
      }
      return Promise.resolve('searchOffersResults')
    }
    default: {
      // if we are on any other page, we will reset the booking state and go back to search results
      bookingCtx.dispatch({ type: 'PARTIAL_RESET_BOOKING_STATE' })
      return Promise.resolve('searchOffersResults')
    }
  }
}

export const continueNavigation = async (
  bookingCtx: TBookingContextDef,
  accountName: string
): Promise<TRouteNames | undefined> => {
  switch (bookingCtx.state.currentState) {
    // if we are on search results, we need to check if there are offers or petAddOns to know
    // where to jump next, to keep browser history clean
    case 'searchResults': {
      let rc = await checkOfferAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      // this will check for grooming too if enabled
      rc = await checkPetAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }

      return routeAfterBookingConfiguration(bookingCtx)
    }
    // page that show the results of the search for grooming
    case 'searchResultsGrooming': {
      if (hasRequestDates(bookingCtx.state.config.serviceType)) {
        return 'groomingWizard'
      }

      return routeAfterBookingConfiguration(bookingCtx)
    }
    case 'grooming': {
      return routeAfterBookingConfiguration(bookingCtx)
    }
    // if we are on offerAddOns, we need to check if there are petAddOns to jump to that page
    // or skip it
    case 'roomAddOns': {
      const rc = await checkPetAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      return routeAfterBookingConfiguration(bookingCtx)
    }
    case 'petAddOns': {
      removePerksWithZeroQuantity(bookingCtx)
      if (hasGroomingServiceType(bookingCtx) && hasGroomsSelected(bookingCtx)) {
        return 'groomingWizard'
      }

      return routeAfterBookingConfiguration(bookingCtx)
    }
    case 'arrivalDepartures': {
      return 'detailsPolicies'
    }
    // dates selection for DayCare
    case 'datesSelection': {
      const rc = await checkPetAddOnsDaycare(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      // if (!bookingCtx.state.groups?.[0]?.pets?.[0].temperTested) {
      if (isOfferWithTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      return routeAfterBookingConfiguration(bookingCtx)
    }
    case 'temperTest': {
      return routeAfterBookingConfiguration(bookingCtx)
    }
    case 'detailsPolicies': {
      return 'paymentForm'
    }
  }

  return
}

export const removePerksWithZeroQuantity = (bookingCtx: TBookingContextDef) => {
  bookingCtx.state.cart.groups.forEach((group, groupIdx) => {
    group.petsAddOn?.forEach((petAddOn) => {
      petAddOn.pets.forEach((pet) => {
        if (pet.qty === 0) {
          bookingCtx.dispatch({
            type: 'REMOVE_PET_ADDON',
            payload: {
              groupIdx,
              petAddOnId: petAddOn.offer.id,
              petId: pet.id,
            },
          })
        }
      })
    })
  })
}

const isOfferWithTemperTest = (bookingCtx: TBookingContextDef) => {
  return bookingCtx.state.cart.groups.some((g) => {
    return (
      g.offerSelected?.availability.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST' ||
      g.daycareSelected?.some((day) => day.availability.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST')
    )
  })
}

const filterAvailableAddOns = (results: BookingSearchClientDto[]) =>
  results.filter((addOn) =>
    addOn.availabilityGroups?.some((group) => group.availabilities?.some((av) => !!av.available))
  )

const someAddOnsAvailable = (results: BookingSearchByDaysAdminDto[]) =>
  results.some((addOn) =>
    addOn.availabilityGroupsByDays?.some((group) => group[0].availabilities?.some((av) => av.available))
  )

const checkOfferAddOns = async (
  bookingCtx: TBookingContextDef,
  accountName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const services = await ClientBookingSearchService.search(accountName, serviceTypeName, {
    ...(buildSearchParams(bookingCtx, true) as TBookingSearchOptions),
    offersType: 'SERVICE',
  })
  const availableAddOns = filterAvailableAddOns(services.results)
  if (availableAddOns.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_OFFERS_ADDONS',
      payload: { value: sortOrders(availableAddOns), searchState: 'roomAddOns' },
    })
    return 'searchRoomAddOns'
  }
  return
}

const performSearch = async (
  locationName: string,
  serviceTypeName: string,
  params: TBookingSearchOptions,
  offersType: string
): Promise<BookingSearchClientDto[] | BookingSearchByDaysAdminDto[] | undefined> => {
  try {
    if (serviceTypeName === 'daycare' && offersType === 'PET') {
      const result = await ClientBookingSearchService.searchByDays(locationName, serviceTypeName, {
        ...params,
        offersType,
      } as TBookingSearchOptions)
      return result.results
    } else {
      const result = await ClientBookingSearchService.search(locationName, serviceTypeName, {
        ...params,
        offersType,
      } as TBookingSearchOptions)
      return result.results
    }
  } catch (error) {
    captureException(new BookingSearchError('Error looking for offers', error as Error))
    return undefined
  }
}

const checkGroomingServices = async (
  bookingCtx: TBookingContextDef,
  locationName: string,
  baseParams: TBookingSearchOptions
): Promise<BookingSearchClientDto[]> => {
  if (!hasGroomingServiceType(bookingCtx)) {
    return []
  }

  const { startDate: _sd, endDate: _ed, ...params } = baseParams

  params.petGroups = buildPetGroups(bookingCtx.state, false, true) as TBookingSearchPetGroup[]
  params.offersType = 'PRIMARY'

  try {
    const result = await ClientBookingSearchService.searchByDays(
      locationName,
      'grooming',
      params as TBookingSearchOptions
    )
    return result.results || []
  } catch (error) {
    captureException(new BookingSearchError('Error looking for offers', error as Error))
    return []
  }
}

const checkPetAddOns = async (
  bookingCtx: TBookingContextDef,
  locationName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const searchParams = buildSearchParams(bookingCtx, true)
  const hasInStayGrooms = hasGroomingServiceType(bookingCtx)

  const [petAddOns, groomingServices] = await Promise.all([
    performSearch(locationName, serviceTypeName, searchParams as TBookingSearchOptions, 'PET'),
    hasInStayGrooms
      ? checkGroomingServices(bookingCtx, locationName, searchParams as TBookingSearchOptions)
      : Promise.resolve<BookingSearchClientDto[]>([]),
  ])

  const availablePetAddOns = (petAddOns || []) as BookingSearchClientDto[]

  if (availablePetAddOns.length > 0 || groomingServices.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_PET_ADDONS',
      payload: {
        value: sortOrders(availablePetAddOns),
        searchState: 'petAddOns',
        grooms: groomingServices,
      },
    })
    return 'searchPetAddOns'
  }

  return undefined
}

const checkPetAddOnsDaycare = async (
  bookingCtx: TBookingContextDef,
  locationName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const dayPeriod = daycareSelectionPeriod(bookingCtx)

  if (!dayPeriod) {
    return undefined
  }

  const searchParams = {
    petGroups: buildPetGroups(bookingCtx.state, true),
    offersType: 'PET',
    startDate: dayPeriod.startDate,
    endDate: dayPeriod.endDate,
  } as TBookingSearchOptions

  const [petAddOns, groomingServices] = await Promise.all([
    performSearch(locationName, serviceTypeName, searchParams, 'PET'),
    checkGroomingServices(bookingCtx, locationName, searchParams),
  ])

  const hasAvailablePetAddOns = petAddOns ? someAddOnsAvailable(petAddOns as BookingSearchByDaysAdminDto[]) : false

  if (hasAvailablePetAddOns || groomingServices.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_PET_ADDONS',
      payload: {
        value: sortOrders((petAddOns || []) as BookingSearchClientDto[]),
        searchState: 'petAddOns',
        grooms: groomingServices,
      },
    })
    return 'searchPetAddOns'
  }

  return undefined
}

export const buildSearchParams = (
  bookingCtx: TBookingContextDef,
  isAddOnSearch?: boolean,
  isGroom?: boolean,
  groomId?: string
) => {
  return {
    startDate: format(bookingCtx.state.startDate || 0, 'yyyy-MM-dd'),
    endDate: format(bookingCtx.state.endDate || 0, 'yyyy-MM-dd'),
    petGroups: buildPetGroups(bookingCtx.state, isAddOnSearch, isGroom, groomId),
    disableHatch: isHatchDisabledForServiceType(bookingCtx.state),
  }
}
export const buildSearchParamsForTemperTest = (
  bookingCtx: TBookingContextDef,
  today: Date,
  tempTestRange: Date
): TBookingSearchOptions => {
  return {
    startDate: format(today || 0, 'yyyy-MM-dd'),
    endDate: format(tempTestRange || 0, 'yyyy-MM-dd'),
    petGroups: buildPetGroupsTempered(bookingCtx.state) as TBookingSearchPetGroup[],
    offersType: 'PRIMARY',
  }
}
